import React from 'react';
import { useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Checkbox } from 'ui-components/components/checkbox';
import { Pill } from 'ui-components/components/pill';
import { noop } from 'ui-components/default-props/noop';
import { Tag as OrdersTagActionableFilterPill } from '../../../../components/actionable-filter-pill/connected/orders/tag';
import { RemoveAllFiltersDropdownMenuItem } from '../../../../components/remove-all-filters-dropdown-menu-item';
import { OptionFormTypes } from '../../../../services/filtering/filter-form-render-service/option-form-types';
import { containsIndicator } from '../../../../services/filtering/collection-filter-mapping-service';
import Global from '../../../../store/global-selectors';
import { messages } from './messages';
import { TAG } from '../../../../filters/constants';
import style from '../common-styles.module.less';

const isTagSelected = (selectedTags, tag) => {
  const tagIndex = selectedTags.findIndex((selectedTag) => selectedTag.tagId === tag.tagId);
  return tagIndex !== -1;
};

const TagComponent = React.memo(({ criteriaInformations, existingFiltersHelpers }) => {
  const { onApplyFilter, onRemoveFilter, existingFilters, removeAllOfTheseFilters } = existingFiltersHelpers;
  const tags = useSelector(Global.selectSellerTagsAsDictionary);
  const { selectedFilterBy, criterionArgumentOptions } = criteriaInformations;
  const availableTags = criterionArgumentOptions || [];
  const selectedTags = existingFilters
    .map(({ criterionMeta, criterionArgumentValue: tagId }) => criterionMeta || tags[tagId])
    .filter((tag) => !!tag);

  const onTagToggled = (tag) => {
    if (!isTagSelected(selectedTags, tag)) {
      const containsCriterionOperator = { value: containsIndicator };
      const criteriaArgument = { value: tag.tagId };
      onApplyFilter(selectedFilterBy, containsCriterionOperator, criteriaArgument, tag);
      return;
    }
    const existingFilter = existingFilters.find((filter) => filter.criterionArgumentValue === tag.tagId);
    onRemoveFilter(existingFilter);
  };

  if (!availableTags || availableTags.length === 0) {
    return null;
  }

  const hasExistingFilters = !!existingFilters.length;

  return (
    <div>
      <RemoveAllFiltersDropdownMenuItem hasExistingFilters={hasExistingFilters} onRemoveAllFiltersButtonClick={removeAllOfTheseFilters} />
      {availableTags.map((tag) => (
        <DropdownMenuItem key={tag.tagId} className={style['checklist-row']} onClick={() => onTagToggled(tag)}>
          <Checkbox checked={isTagSelected(selectedTags, tag)} onChange={noop} />
          <Pill className={style.pill} color={tag.hexColor} label={tag.name} theme='no-status' truncate />
        </DropdownMenuItem>
      ))}
    </div>
  );
});

TagComponent.propTypes = {
  criteriaInformations: PropTypes.shape({
    selectedFilterBy: PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.node,
      filterValue: PropTypes.string,
      shouldHideActionButtons: PropTypes.bool,
      expectsArgumentAsArray: PropTypes.bool,
      render: PropTypes.func,
    }).isRequired,
    criterionArgumentOptions: PropTypes.arrayOf(
      PropTypes.shape({
        tagId: PropTypes.string,
        name: PropTypes.string,
        hexColor: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  existingFiltersHelpers: PropTypes.shape({
    onApplyFilter: PropTypes.func.isRequired,
    onRemoveFilter: PropTypes.func.isRequired,
    existingFilters: PropTypes.array.isRequired,
    removeAllOfTheseFilters: PropTypes.func.isRequired,
  }).isRequired,
};

export const Tag = {
  id: TAG,
  displayName: <FormattedMessage {...messages.tag} />,
  filterValue: 'tag',
  type: OptionFormTypes.TYPE_ORDER_COLLECTION_FILTER,
  allowsMultipleSelection: true,
  expectsArgumentAsArray: true,
  shouldHideActionButtons: true,

  render: (criteriaInformations, existingFiltersHelpers) => (
    <TagComponent criteriaInformations={criteriaInformations} existingFiltersHelpers={existingFiltersHelpers} />
  ),

  actionableFilterPill: OrdersTagActionableFilterPill,
};
